import type { Strings } from '@llura/constants';

import type { CoachGender, CoachTone } from '../stores/coach.store';

/**
 * Coach persona pool — first names + avatars only. Voice, taglines, and daily
 * guidance live in the i18n strings tree ({@link Strings.coach}). The chat
 * opener and replies are LLM-generated; no scripted text lives here.
 * Functions that need text take a `strings` parameter so they stay pure and
 * locale-aware.
 *
 * Avatar URLs are elegant editorial portraits on Unsplash, served at 512px.
 */

const NAMES: Record<CoachGender, Record<CoachTone, readonly string[]>> = {
  female: {
    dominant: ['Ava', 'Vex', 'Noor'],
    playful: ['Roxy', 'Juno', 'Cleo'],
    sensual: ['Selene', 'Amara', 'Liv'],
  },
  male: {
    dominant: ['Christian', 'Damon', 'Ren'],
    playful: ['Leo', 'Kai', 'Theo'],
    sensual: ['Eli', 'River', 'Jude'],
  },
  nb: {
    dominant: ['Ash', 'Storm', 'Onyx'],
    playful: ['Sage', 'Pip', 'Lou'],
    sensual: ['Wren', 'Lior', 'Sol'],
  },
};

const AVATARS: Record<CoachGender, Record<CoachTone, readonly string[]>> = {
  female: {
    dominant: [
      'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=512&q=80&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1614283233556-f35b0c801ef1?w=512&q=80&auto=format&fit=crop',
    ],
    playful: [
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=512&q=80&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=512&q=80&auto=format&fit=crop',
    ],
    sensual: [
      'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=512&q=80&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=512&q=80&auto=format&fit=crop',
    ],
  },
  male: {
    dominant: [
      'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=512&q=80&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=512&q=80&auto=format&fit=crop',
    ],
    playful: [
      'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=512&q=80&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1463453091185-61582044d556?w=512&q=80&auto=format&fit=crop',
    ],
    sensual: [
      'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=512&q=80&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1507591064344-4c6ce005b128?w=512&q=80&auto=format&fit=crop',
    ],
  },
  nb: {
    dominant: [
      'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=512&q=80&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1485875437342-9b39470b3d95?w=512&q=80&auto=format&fit=crop',
    ],
    playful: [
      'https://images.unsplash.com/photo-1496440737103-cd596325d314?w=512&q=80&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=512&q=80&auto=format&fit=crop',
    ],
    sensual: [
      'https://images.unsplash.com/photo-1521252659862-eec69941b071?w=512&q=80&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1463453091185-61582044d556?w=512&q=80&auto=format&fit=crop',
    ],
  },
};

/**
 * Curated look pool for the in-onboarding picker — 9 portraits per gender,
 * tone-agnostic. Replaces the auto-pick (`pickCoachAvatar`) for new users.
 * Later this will be augmented by user-uploaded / AI-generated images.
 */
export const COACH_LOOKS: Record<CoachGender, readonly string[]> = {
  female: [
    'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1614283233556-f35b0c801ef1?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=512&q=80&auto=format&fit=crop',
  ],
  male: [
    'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1463453091185-61582044d556?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1507591064344-4c6ce005b128?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1488161628813-04466f872be2?w=512&q=80&auto=format&fit=crop',
  ],
  nb: [
    'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1485875437342-9b39470b3d95?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1496440737103-cd596325d314?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1521252659862-eec69941b071?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1463453091185-61582044d556?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1492288991661-058aa541ff43?w=512&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1601412436009-d964bd02edbc?w=512&q=80&auto=format&fit=crop',
  ],
};

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function pick<T>(list: readonly T[], seed: string): T {
  return list[hashString(seed) % list.length];
}

export function pickCoachName(gender: CoachGender, tone: CoachTone, seed: string): string {
  return pick(NAMES[gender][tone], seed + ':name');
}

export function pickCoachAvatar(gender: CoachGender, tone: CoachTone, seed: string): string {
  return pick(AVATARS[gender][tone], seed + ':avatar');
}

/**
 * Default uses the `fordernd` pool — the adaptive selector (phase 4) will pick
 * between `sanft` and `fordernd` based on the user's recent activity.
 */
export function buildDailyCommand(strings: Strings, tone: CoachTone, seed = ''): string {
  return pick(strings.coach.dailyGuidance[tone].fordernd, (seed || 'today') + ':command');
}

/** Visual accent per tone — text labels come from i18n strings. */
export const TONE_ACCENT: Record<CoachTone, 'primary' | 'secondary' | 'accent'> = {
  dominant: 'primary',
  playful: 'secondary',
  sensual: 'accent',
};
